/** 
 * DISCOUNT_ELECTRICIAN_LandingPage_v020c_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v020c
 * Date: 2025-09-04 (America/Denver)
 *
 * Notes:
 * - This file is Part A of a 3-part paste pack. Paste A, then B, then C into ONE .tsx file.
 * - Minimal validation; all submitted values are STRINGS for wire payload.
 * - Wizard & Vertical modes with identical sections; confirmation is sectioned (no raw JSON).
 */

import React, { useMemo, useRef, useState, ReactNode } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger, Tooltip,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,  
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v020c";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/ELECTRICIAN-Insurance/";
                   
/* ============================================================
   TYPES
============================================================ */
type Deductible = "" | "0" | "250" | "500" | "1000" | "2500";
type WorkEnv = "" | "shop" | "mobile" | "both";
type ElectricService = "residential" | "commercial" | "panel_upgrades" | "rewiring" | "ev_chargers" | "generator_install" | "solar_tie_in" | "low_voltage" | "other";
type HeightLevel = "" | "ground_only" | "up_to_two_stories" | "over_two_stories";

/** Raw lead payload. NOTE: everything that can be text is a string. */
interface ILeadPayload {
  // Contact & Business
  fullName: string;
  email: string;
  phone: string;

  businessName: string;
  yearsInBusiness: string;         // stringified
  employeesCount: string;          // stringified
  website?: string;

  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  // Operations & Exposures
  workEnv: WorkEnv;
  services: ElectricService[];
  licenseState?: string;
  licenseNumber?: string;
  workAtHeights: HeightLevel;
  newConstruction: "yes" | "no";
  serviceCalls: "yes" | "no";
  knobAndTube?: "yes" | "no";
  aluminumWiring?: "yes" | "no";
  mobileRadiusMiles?: string;

  annualRevenue: string;
  employeePayroll: string;           // renamed
  subcontractorAnnualCost: string;   // renamed

  // Coverage & Safety
  glPerOccurrence: string;
  glAggregate: string;
  toolsLimit: string;
  inlandMarine: "yes" | "no";
  commercialAuto: "yes" | "no";
  workersComp: "yes" | "no";
  deductible: Deductible;
  additionalInsureds: string;      // stringified count
  needCOIs: "yes" | "no";

  oshaTraining: "yes" | "no";
  ppeEnforced: "yes" | "no";
  lockoutTagout: "yes" | "no";
  arcFlashPPE: "yes" | "no";
  permitPulled: "yes" | "no";

  priorClaimsCount5y: string;      // stringified count
  lastClaimDate?: string;

  // Notes / Files
  notes?: string;
  fileUrls?: string;

  // Meta
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;

  utm?: { source?: string; medium?: string; campaign?: string };
}

/** Wire payload where EVERY VALUE IS A STRING */
type WirePayload = Record<string, string>;

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const SERVICE_LABELS: Record<ElectricService, string> = {
  residential: "Residential",
  commercial: "Commercial",
  panel_upgrades: "Panel upgrades",
  rewiring: "Rewiring",
  ev_chargers: "EV chargers",
  generator_install: "Generator installs",
  solar_tie_in: "Solar tie-in",
  low_voltage: "Low voltage / data",
  other: "Other",
};

const money = (nLike?: string) => {
  const digits = (nLike ?? "").replace(/[^\d.]/g, "");
  const n = Number(digits || "0");
  if (!Number.isFinite(n) || n <= 0) return "—";
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  } catch {
    return `$${n}`;
  }
};
function isValidEmail(email: string): boolean {
  return /.+@.+\..+/.test(email);
}
function dash(v?: string | number | boolean | null) {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s.length ? s : "—";
}

/* ============================================================
   SLIDES
============================================================ */
interface Slide { src: string; alt: string; title: ReactNode; subline: string; smallCaption: string; }

const slides: Slide[] = [ 
  {
    src: imagePath + "AdobeStock_593220908.jpg",
    alt: "Electrician working on breaker panel.",
    title: "Protect Your Electrical Business",
    subline: "General liability, tools & equipment, and more—at a discount.",
    smallCaption: "Fast quotes. Clear coverage.",
  },
  {
    src: imagePath + "AdobeStock_298771834.jpg",
    alt: "EV charger installation in garage.",
    title: "Residential & Commercial",
    subline: "From panel upgrades to EV chargers and service calls.",
    smallCaption: "Flexible limits.",
  },
  {
    src: imagePath + "AdobeStock_331808504.jpg",
    alt: "Electrician on a ladder with PPE.",
    title: "Safety First, Better Rates",
    subline: "Rewarded for OSHA, LOTO, and arc-flash PPE.",
    smallCaption: "Licensed advisors.",
  },
  {
    src: imagePath + "AdobeStock_227756596.jpg",
    alt: "Electrical tools neatly organized.",
    title: "Protect Your Tools & Gear",
    subline: "Coverage for meters, ladders, testers, and more.",
    smallCaption: "Don’t pay out-of-pocket for theft or damage.",
  },
];

/* ============================================================
   MAIN
============================================================ */
export const DISCOUNT_ELECTRICIAN_LandingPage_v020c_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact & Business (strings)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [yearsInBusiness, setYearsInBusiness] = useState<string>("0");
  const [employeesCount, setEmployeesCount] = useState<string>("0");
  const [website, setWebsite] = useState<string>("");

  // Address (strings)
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Operations & Exposures
  const [workEnv, setWorkEnv] = useState<WorkEnv>("");
  const [services, setServices] = useState<ElectricService[]>([]);
  const [licenseState, setLicenseState] = useState<string>("");
  const [licenseNumber, setLicenseNumber] = useState<string>("");
  const [workAtHeights, setWorkAtHeights] = useState<HeightLevel>("");
  const [newConstruction, setNewConstruction] = useState<"yes" | "no">("yes");
  const [serviceCalls, setServiceCalls] = useState<"yes" | "no">("yes");
  const [knobAndTube, setKnobAndTube] = useState<"yes" | "no">("no");
  const [aluminumWiring, setAluminumWiring] = useState<"yes" | "no">("no");
  const [mobileRadiusMiles, setMobileRadiusMiles] = useState<string>("");

  // Simple money-ish strings
  const [annualRevenue, setAnnualRevenue] = useState<string>("150000");
  const [employeePayroll, setEmployeePayroll] = useState<string>("80000");
  const [subcontractorAnnualCost, setSubcontractorAnnualCost] = useState<string>("0");

  // Coverage & Safety (strings + yes/no)
  const [glPerOccurrence, setGlPerOccurrence] = useState<string>("1000000");
  const [glAggregate, setGlAggregate] = useState<string>("2000000");
  const [toolsLimit, setToolsLimit] = useState<string>("10000");
  const [inlandMarine, setInlandMarine] = useState<"yes" | "no">("yes");
  const [commercialAuto, setCommercialAuto] = useState<"yes" | "no">("no");
  const [workersComp, setWorkersComp] = useState<"yes" | "no">("no");
  const [deductible, setDeductible] = useState<Deductible>("500");
  const [additionalInsureds, setAdditionalInsureds] = useState<string>("0");
  const [needCOIs, setNeedCOIs] = useState<"yes" | "no">("yes");

  const [oshaTraining, setOshaTraining] = useState<"yes" | "no">("yes");
  const [ppeEnforced, setPpeEnforced] = useState<"yes" | "no">("yes");
  const [lockoutTagout, setLockoutTagout] = useState<"yes" | "no">("yes");
  const [arcFlashPPE, setArcFlashPPE] = useState<"yes" | "no">("yes");
  const [permitPulled, setPermitPulled] = useState<"yes" | "no">("yes");

  const [priorClaimsCount5y, setPriorClaimsCount5y] = useState<string>("0");
  const [lastClaimDate, setLastClaimDate] = useState<string>("");

  // Notes / Files
  const [notes, setNotes] = useState<string>("");
  const [fileUrls, setFileUrls] = useState<string>("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);
  const [lastSubmittedApp, setLastSubmittedApp] = useState<ILeadPayload | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Operations & Exposures", "Coverage, Safety & Notes", "Confirmation"] as const;

  /* ---------- Minimal validation ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });
  const vpOpt = (name: string, valuePresent: boolean, passIfPresent: boolean = true) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && valuePresent && passIfPresent,
    isInvalid: isTouched(name) && valuePresent && !passIfPresent,
    className: isTouched(name) && valuePresent && passIfPresent ? "bg-success-subtle" : undefined,
  });

  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: phone.trim().length >= 7, // simplified
    businessName: businessName.trim().length >= 2,

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: zipCode.trim().length >= 3,

    workEnv: workEnv !== "",
    services: services.length > 0,
    workAtHeights: workAtHeights !== "",

    annualRevenue: annualRevenue.trim().length > 0,
    employeePayroll: employeePayroll.trim().length > 0,
    subcontractorAnnualCost: subcontractorAnnualCost.trim().length >= 0, // optional OK

    glPerOccurrence: glPerOccurrence.trim().length > 0,
    glAggregate: glAggregate.trim().length > 0,
    toolsLimit: toolsLimit.trim().length >= 0,
    deductible: deductible !== "",
    additionalInsureds: additionalInsureds.trim().length >= 0,

    priorClaimsCount5y: priorClaimsCount5y.trim().length >= 0,
    lastClaimDate: true, // optional
    notes: true,         // optional
    fileUrls: true,      // optional
    mobileRadius: true,  // optional
  };

  const valid = useMemo(() => {
    return (
      V.fullName && V.email && V.phone &&
      V.businessName &&
      V.address1 && V.city && V.state && V.zip &&
      V.workEnv && V.services && V.workAtHeights &&
      V.annualRevenue && V.employeePayroll &&
      V.glPerOccurrence && V.glAggregate && V.toolsLimit && V.deductible
    );
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.businessName && V.address1 && V.city && V.state && V.zip;
      case 1:
        return V.workEnv && V.services && V.workAtHeights && V.annualRevenue && V.employeePayroll;
      case 2:
        return V.glPerOccurrence && V.glAggregate && V.toolsLimit && V.deductible;
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","businessName","address1","city","state","zip"]);
    else if (currentStep === 1) touchMany(["workEnv","services","workAtHeights","annualRevenue","employeePayroll"]);
    else if (currentStep === 2) touchMany(["glPerOccurrence","glAggregate","toolsLimit","deductible"]);

    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  /* ============================================================
     PAYLOAD & SUBMIT (EVERYTHING => strings)
  ============================================================ */
  function buildLeadPayload(): ILeadPayload {
    return {
      // Contact & Business
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),

      businessName: businessName.trim(),
      yearsInBusiness: yearsInBusiness.trim(),
      employeesCount: employeesCount.trim(),
      website: website?.trim() || "",

      // Address
      address1: address1.trim(),
      address2: address2?.trim() || "",
      city: city.trim(),
      state: String(state),
      zipCode: String(zipCode),

      // Operations
      workEnv,
      services,
      licenseState: licenseState?.trim() || "",
      licenseNumber: licenseNumber?.trim() || "",
      workAtHeights,
      newConstruction,
      serviceCalls,
      knobAndTube,
      aluminumWiring,
      mobileRadiusMiles: mobileRadiusMiles?.trim() || "",

      // Revenue / Payroll
      annualRevenue: annualRevenue.trim(),
      employeePayroll: employeePayroll.trim(),
      subcontractorAnnualCost: subcontractorAnnualCost.trim(),

      // Coverage & Safety
      glPerOccurrence: glPerOccurrence.trim(),
      glAggregate: glAggregate.trim(),
      toolsLimit: toolsLimit.trim(),
      inlandMarine,
      commercialAuto,
      workersComp,
      deductible,
      additionalInsureds: additionalInsureds.trim(),
      needCOIs,
      oshaTraining,
      ppeEnforced,
      lockoutTagout,
      arcFlashPPE,
      permitPulled,

      priorClaimsCount5y: priorClaimsCount5y.trim(),
      lastClaimDate: (lastClaimDate || "").trim(),

      // Notes / Files
      notes: notes?.trim() || "",
      fileUrls: fileUrls?.trim() || "",

      // Meta
      domainName: typeof window !== "undefined" ? String(window.location.hostname) : "unknown",
      itemCategory: "Customer.electrician.quote",
      itemClass: "quote.request",
      itemType: "insurance.electrician.landing",
      clientVersion: String(VERSION),
    };
  }

  function toWirePayloadStrings(p: ILeadPayload): WirePayload {
    const out: WirePayload = {};
    for (const [k, v] of Object.entries(p)) {
      if (typeof v === "string") out[k] = v;
      else if (Array.isArray(v)) {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else if (v && typeof v === "object") {
        try { out[k] = JSON.stringify(v); } catch { out[k] = ""; }
      } else {
        out[k] = v == null ? "" : String(v);
      }
    }
    return out;
  }

  async function postLead(payload: WirePayload): Promise<{ ok: boolean; status: number; id?: string; message?: string }> {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      mode: "cors",
    });

    let resJson: unknown = null;
    try {
      const txt = await res.text();
      resJson = txt ? JSON.parse(txt) : null;
    } catch {
      resJson = null;
    }

    const asObj = (x: unknown): Record<string, unknown> | null =>
      x && typeof x === "object" && !Array.isArray(x) ? (x as Record<string, unknown>) : null;

    if (!res.ok) {
      const o = asObj(resJson);
      const msg = (o?.error as string) || (o?.message as string) || `Request failed: ${res.status}`;
      return { ok: false, status: res.status, message: msg };
    }

    const o = asObj(resJson);
    const id = (o?.id as string) || (o?.requestId as string) || undefined;
    return { ok: true, status: res.status, id };
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // Light-touch: mark some core fields
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      businessName: true, address1: true, city: true, state: true, zip: true,
      workEnv: true, services: true, workAtHeights: true,
      annualRevenue: true, employeePayroll: true,
      glPerOccurrence: true, glAggregate: true, toolsLimit: true, deductible: true,
    }));

    if (!valid) {
      setSubmitError("Please complete required fields marked in red.");
      return;
    }

    const appPayload = buildLeadPayload();
    const wirePayload = toWirePayloadStrings(appPayload);

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload (wire):", wirePayload);
        await new Promise((r) => setTimeout(r, 500));
        const status = { ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) };
        setServerStatus(status);
        setLastSubmittedApp(appPayload);
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const status = await postLead(wirePayload);
      setServerStatus(status);
      if (!status.ok) throw new Error(status.message || "Unknown server error");

      setLastSubmittedApp(appPayload);
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  // Clamp active slide index
  const active = slides[Math.min(slides.length - 1, Math.max(0, Number.isFinite(activeSlide) ? activeSlide : 0))];

  /* ------------------------------------------------------------
     UI (HERO + FORM SHELL)
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount <span className="text-primary">Electrician</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an advisor:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(typeof i === "number" ? i : 0)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-0 g-sm-4 p-0 p-sm-4 p-lg-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Residential & Commercial</Badge>
                  <Badge bg="light" text="dark">COIs & AI Endorsements</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#faq">Common Questions</Button>
                </div>
              </div>
            </Col>

            <Col lg={6}>
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast, No-Obligation Quote</div>
                      <small className="text-body-secondary">Tell us about your operation for tailored options.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>
                      Server: {submitError}
                    </Alert>
                  )

/* ==== END PART A ==== */
                }


                {/* ==== PART B: Mode Toggle, Form, and Section Components ==== */}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                          else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  {/* FORM */}
                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <SectionCard title="Contact & Business">
                                <SectionContactBusiness
                                  values={{
                                    fullName, email, phone,
                                    businessName, yearsInBusiness, employeesCount, website,
                                    address1, address2, city, state, zipCode,
                                  }}
                                  setters={{
                                    setFullName, setEmail, setPhone,
                                    setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite,
                                    setAddress1, setAddress2, setCity, setState, setZipCode,
                                  }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 1 && (
                            <Col xs={12}>
                              <SectionCard title="Operations & Exposures">
                                <SectionOperations
                                  values={{
                                    workEnv, services, licenseState, licenseNumber, workAtHeights,
                                    newConstruction, serviceCalls, knobAndTube, aluminumWiring, mobileRadiusMiles,
                                    annualRevenue, employeePayroll, subcontractorAnnualCost,
                                  }}
                                  setters={{
                                    setWorkEnv, setServices, setLicenseState, setLicenseNumber, setWorkAtHeights,
                                    setNewConstruction, setServiceCalls, setKnobAndTube, setAluminumWiring, setMobileRadiusMiles,
                                    setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
                                  }}
                                  V={V}
                                  vp={vp}
                                  vpOpt={vpOpt}
                                />
                              </SectionCard>
                            </Col>
                          )}

                          {currentStep === 2 && (
                            <>
                              <Col xs={12}>
                                <SectionCard title="Coverage & Safety">
                                  <SectionCoverageSafety
                                    values={{
                                      glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
                                      deductible, additionalInsureds, needCOIs,
                                      oshaTraining, ppeEnforced, lockoutTagout, arcFlashPPE, permitPulled,
                                      priorClaimsCount5y, lastClaimDate,
                                    }}
                                    setters={{
                                      setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
                                      setDeductible, setAdditionalInsureds, setNeedCOIs,
                                      setOshaTraining, setPpeEnforced, setLockoutTagout, setArcFlashPPE, setPermitPulled,
                                      setPriorClaimsCount5y, setLastClaimDate,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                              <Col xs={12}>
                                <SectionCard title="Notes">
                                  <SectionNotesFiles
                                    values={{ notes, fileUrls }}
                                    setters={{ setNotes, setFileUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </>
                          )}

                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button
                              type="button"
                              variant="outline-secondary"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                goBack();
                              }}
                              disabled={currentStep === 0}
                            >
                              Previous
                            </Button>

                            {currentStep < 2 ? (
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isStepValid(currentStep)) goNext();
                                }}
                                disabled={!isStepValid(currentStep)}
                              >
                                Next
                              </Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // VERTICAL VIEW (reuse same sections)
                      <>
                        {!submitOk ? (
                          <>
                            <Row className="g-3">
                              <Col xs={12}>
                                <SectionCard title="Contact & Business">
                                  <SectionContactBusiness
                                    values={{
                                      fullName, email, phone,
                                      businessName, yearsInBusiness, employeesCount, website,
                                      address1, address2, city, state, zipCode,
                                    }}
                                    setters={{
                                      setFullName, setEmail, setPhone,
                                      setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite,
                                      setAddress1, setAddress2, setCity, setState, setZipCode,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Operations & Exposures">
                                  <SectionOperations
                                    values={{
                                      workEnv, services, licenseState, licenseNumber, workAtHeights,
                                      newConstruction, serviceCalls, knobAndTube, aluminumWiring, mobileRadiusMiles,
                                      annualRevenue, employeePayroll, subcontractorAnnualCost,
                                    }}
                                    setters={{
                                      setWorkEnv, setServices, setLicenseState, setLicenseNumber, setWorkAtHeights,
                                      setNewConstruction, setServiceCalls, setKnobAndTube, setAluminumWiring, setMobileRadiusMiles,
                                      setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Coverage & Safety">
                                  <SectionCoverageSafety
                                    values={{
                                      glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
                                      deductible, additionalInsureds, needCOIs,
                                      oshaTraining, ppeEnforced, lockoutTagout, arcFlashPPE, permitPulled,
                                      priorClaimsCount5y, lastClaimDate,
                                    }}
                                    setters={{
                                      setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
                                      setDeductible, setAdditionalInsureds, setNeedCOIs,
                                      setOshaTraining, setPpeEnforced, setLockoutTagout, setArcFlashPPE, setPermitPulled,
                                      setPriorClaimsCount5y, setLastClaimDate,
                                    }}
                                    V={V}
                                    vp={vp}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>

                              <Col xs={12}>
                                <SectionCard title="Notes">
                                  <SectionNotesFiles
                                    values={{ notes, fileUrls }}
                                    setters={{ setNotes, setFileUrls }}
                                    V={V}
                                    vpOpt={vpOpt}
                                  />
                                </SectionCard>
                              </Col>
                            </Row>

                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about electrician insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock status={serverStatus} app={lastSubmittedApp} />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to protect your electrical business.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your work</h6>
                  <p className="mb-0 text-body-secondary">Residential/commercial, services, and exposures.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">Pick your coverage</h6>
                  <p className="mb-0 text-body-secondary">GL limits, tools, inland marine, endorsements.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">We shop carriers</h6>
                  <p className="mb-0 text-body-secondary">We help you bind and issue COIs fast.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">Frequently Asked Questions</h2>
              <p className="text-body-secondary mb-0">Short answers to common concerns.</p>
            </Col>
          </Row>

          <SimpleFAQ faqs={ELECTRICIAN_INSURANCE_FAQ_CONTENT_v01} openFirst />
        </Container>
      </section>
      
      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Discount Electrician Insurance. All rights reserved.
              <div className="mt-2">
                Insurance services subject to licensing and state availability. This page is for general information and not legal advice.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

/* ============================================================
   REUSABLE SECTION COMPONENTS
============================================================ */

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>{title}</strong>
    </legend>
    <div className="mt-1">{children}</div>
  </fieldset>
);

/* ---------- Contact & Business ---------- */
const SectionContactBusiness: React.FC<{
  values: {
    fullName: string; email: string; phone: string;
    businessName: string; yearsInBusiness: string; employeesCount: string; website: string;
    address1: string; address2: string; city: string; state: string; zipCode: string;
  };
  setters: {
    setFullName: (v: string) => void; setEmail: (v: string) => void; setPhone: (v: string) => void;
    setBusinessName: (v: string) => void; setYearsInBusiness: (v: string) => void; setEmployeesCount: (v: string) => void; setWebsite: (v: string) => void;
    setAddress1: (v: string) => void; setAddress2: (v: string) => void; setCity: (v: string) => void; setState: (v: string) => void; setZipCode: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const { fullName, email, phone, businessName, yearsInBusiness, employeesCount, website, address1, address2, city, state, zipCode } = values;
  const { setFullName, setEmail, setPhone, setBusinessName, setYearsInBusiness, setEmployeesCount, setWebsite, setAddress1, setAddress2, setCity, setState, setZipCode } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Contact - Full Name </Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="businessName">Business Name</Form.Label>
        <Form.Control id="businessName" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Acme Electric LLC" required {...vp("businessName", V.businessName)} />
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="website">Website (optional)</Form.Label>
        <Form.Control id="website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" {...vpOpt("website", !!website.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Please enter a phone number.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="yib">Years in Business</Form.Label>
        <Form.Control id="yib" inputMode="numeric" value={yearsInBusiness} onChange={(e) => setYearsInBusiness(e.target.value)} placeholder="0" />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="emp">Employees</Form.Label>
        <OverlayTrigger placement="right" overlay={<Tooltip id="NumberOfEmployeesTip">Enter the number of employees on W2 payroll.</Tooltip>}>
          <Form.Control id="emp" inputMode="numeric" value={employeesCount} onChange={(e) => setEmployeesCount(e.target.value)} placeholder="0" />
        </OverlayTrigger>
      </Col>

      <Col md={12}><hr className="my-2" /></Col>

      <Col md={6}>
        <Form.Label htmlFor="address1">Street Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Enter your street address.</Form.Control.Feedback>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="address2">Unit / Suite</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Suite 200" {...vpOpt("address2", !!address2.trim())} />
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Please enter your zip.</Form.Control.Feedback>
      </Col>
    </Row>
  );
};

/* ---------- Operations & Exposures ---------- */
const SectionOperations: React.FC<{
  values: {
    workEnv: WorkEnv; services: ElectricService[]; licenseState: string; licenseNumber: string; workAtHeights: HeightLevel;
    newConstruction: "yes" | "no"; serviceCalls: "yes" | "no"; knobAndTube: "yes" | "no"; aluminumWiring: "yes" | "no"; mobileRadiusMiles: string;
    annualRevenue: string; employeePayroll: string; subcontractorAnnualCost: string;
  };
  setters: {
    setWorkEnv: (v: WorkEnv) => void; setServices: (v: ElectricService[]) => void; setLicenseState: (v: string) => void; setLicenseNumber: (v: string) => void; setWorkAtHeights: (v: HeightLevel) => void;
    setNewConstruction: (v: "yes" | "no") => void; setServiceCalls: (v: "yes" | "no") => void; setKnobAndTube: (v: "yes" | "no") => void; setAluminumWiring: (v: "yes" | "no") => void; setMobileRadiusMiles: (v: string) => void;
    setAnnualRevenue: (v: string) => void; setEmployeePayroll: (v: string) => void; setSubcontractorAnnualCost: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const {
    workEnv, services, licenseState, licenseNumber, workAtHeights,
    newConstruction, serviceCalls, knobAndTube, aluminumWiring, mobileRadiusMiles,
    annualRevenue, employeePayroll, subcontractorAnnualCost,
  } = values;
  const {
    setWorkEnv, setServices, setLicenseState, setLicenseNumber, setWorkAtHeights,
    setNewConstruction, setServiceCalls, setKnobAndTube, setAluminumWiring, setMobileRadiusMiles,
    setAnnualRevenue, setEmployeePayroll, setSubcontractorAnnualCost,
  } = setters;

  const toggleInArray = <T extends string>(arr: T[], val: T, setFn: (v: T[]) => void) => {
    setFn(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const SERVICE_OPTIONS: ElectricService[] = ["residential","commercial","panel_upgrades","rewiring","ev_chargers","generator_install","solar_tie_in","low_voltage","other"];

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="workEnv">Work Environment</Form.Label>
        <Form.Select id="workEnv" value={workEnv} onChange={(e) => setWorkEnv(e.target.value as WorkEnv)} required {...vp("workEnv", V.workEnv)}>
          <option value="">Select</option>
          <option value="shop">Shop</option>
          <option value="mobile">Mobile</option>
          <option value="both">Both</option>
        </Form.Select>
      </Col>

      <Col md={6}>
        <Form.Label>Services (select all that apply)</Form.Label>
        <div className="d-flex flex-wrap gap-2">
          {SERVICE_OPTIONS.map((p) => (
            <Form.Check
              key={p}
              type="checkbox"
              id={`svc-${p}`}
              label={SERVICE_LABELS[p]}
              checked={services.includes(p)}
              onChange={() => toggleInArray(services, p, setServices)}
            />
          ))}
        </div>
        <div className={V.services ? "form-text" : "text-danger small"}>{V.services ? " " : "Select at least one service."}</div>
      </Col>

      <Col md={6}>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label htmlFor="licState">License State (optional)</Form.Label>
            <Form.Select id="licState" value={licenseState} onChange={(e) => setLicenseState(e.target.value)} {...vpOpt("licenseState", !!licenseState)}>
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label htmlFor="licNum">License # (optional)</Form.Label>
            <Form.Control id="licNum" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="e.g., ME-12345" {...vpOpt("licenseNumber", !!licenseNumber)} />
          </Col>
        </Row>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="workAtHeights">Work at Height</Form.Label>
        <Form.Select id="workAtHeights" value={workAtHeights} onChange={(e) => setWorkAtHeights(e.target.value as HeightLevel)} required {...vp("workAtHeights", V.workAtHeights)}>
          <option value="">Select</option>
          <option value="ground_only">Ground only</option>
          <option value="up_to_two_stories">Up to 2 stories</option>
          <option value="over_two_stories">Over 2 stories</option>
        </Form.Select>
      </Col>

      <Col md={4} className="pt-md-4">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="newConstr" type="checkbox" label="New construction" checked={newConstruction === "yes"} onChange={(e) => setNewConstruction(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="svcCalls" type="checkbox" label="Service calls" checked={serviceCalls === "yes"} onChange={(e) => setServiceCalls(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={4} className="pt-md-4">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="knob" type="checkbox" label="Knob-and-tube work" checked={knobAndTube === "yes"} onChange={(e) => setKnobAndTube(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="al" type="checkbox" label="Aluminum wiring work" checked={aluminumWiring === "yes"} onChange={(e) => setAluminumWiring(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="mobileRadius">Mobile Radius</Form.Label>
        <Form.Control id="mobileRadius" inputMode="numeric" value={mobileRadiusMiles} onChange={(e) => setMobileRadiusMiles(e.target.value)} placeholder="e.g., 50 miles" {...vpOpt("mobileRadius", !!mobileRadiusMiles)} />
        <p>(miles, optional)</p>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="revenue">Annual<br/>Revenue</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="revenue" inputMode="numeric" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} placeholder="e.g., 150000" required {...vp("annualRevenue", V.annualRevenue)} />
        </InputGroup>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="payroll">Annual<br/>Employee Payroll</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="payroll" inputMode="numeric" value={employeePayroll} onChange={(e) => setEmployeePayroll(e.target.value)} placeholder="e.g., 80000" required {...vp("employeePayroll", V.employeePayroll)} />
        </InputGroup>
      </Col>

      <Col md={4}>
        <Form.Label htmlFor="subsCost">Subcontractor<br/>Annual Cost</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            id="subsCost"
            inputMode="numeric"
            value={subcontractorAnnualCost}
            onChange={(e) => setSubcontractorAnnualCost(e.target.value)}
            placeholder="e.g., 25000"
            {...vpOpt("subcontractorAnnualCost", subcontractorAnnualCost.trim().length > 0)}
          />
        </InputGroup>
        <div className="form-text">Enter total annual dollars paid to subs (optional).</div>
      </Col>
    </Row>
  );
};

/* ---------- Coverage & Safety ---------- */
const SectionCoverageSafety: React.FC<{
  values: {
    glPerOccurrence: string; glAggregate: string; toolsLimit: string; inlandMarine: "yes" | "no"; commercialAuto: "yes" | "no"; workersComp: "yes" | "no";
    deductible: Deductible; additionalInsureds: string; needCOIs: "yes" | "no";
    oshaTraining: "yes" | "no"; ppeEnforced: "yes" | "no"; lockoutTagout: "yes" | "no"; arcFlashPPE: "yes" | "no"; permitPulled: "yes" | "no";
    priorClaimsCount5y: string; lastClaimDate: string;
  };
  setters: {
    setGlPerOccurrence: (v: string) => void; setGlAggregate: (v: string) => void; setToolsLimit: (v: string) => void; setInlandMarine: (v: "yes" | "no") => void; setCommercialAuto: (v: "yes" | "no") => void; setWorkersComp: (v: "yes" | "no") => void;
    setDeductible: (v: Deductible) => void; setAdditionalInsureds: (v: string) => void; setNeedCOIs: (v: "yes" | "no") => void;
    setOshaTraining: (v: "yes" | "no") => void; setPpeEnforced: (v: "yes" | "no") => void; setLockoutTagout: (v: "yes" | "no") => void; setArcFlashPPE: (v: "yes" | "no") => void; setPermitPulled: (v: "yes" | "no") => void;
    setPriorClaimsCount5y: (v: string) => void; setLastClaimDate: (v: string) => void;
  };
  V: any;
  vp: (name: string, pass: boolean) => any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, V, vp, vpOpt }) => {
  const {
    glPerOccurrence, glAggregate, toolsLimit, inlandMarine, commercialAuto, workersComp,
    deductible, additionalInsureds, needCOIs,
    oshaTraining, ppeEnforced, lockoutTagout, arcFlashPPE, permitPulled,
    priorClaimsCount5y, lastClaimDate,
  } = values;
  const {
    setGlPerOccurrence, setGlAggregate, setToolsLimit, setInlandMarine, setCommercialAuto, setWorkersComp,
    setDeductible, setAdditionalInsureds, setNeedCOIs,
    setOshaTraining, setPpeEnforced, setLockoutTagout, setArcFlashPPE, setPermitPulled,
    setPriorClaimsCount5y, setLastClaimDate,
  } = setters;

  return (
    <Row className="g-3">
      <Col md={6}>
        <Form.Label htmlFor="glOcc">GL Per-Occurrence Limit</Form.Label>
        <Form.Select id="glOcc" value={glPerOccurrence} onChange={(e) => setGlPerOccurrence(e.target.value)} required {...vp("glPerOccurrence", V.glPerOccurrence)}>
          <option value="500000">$500,000</option>
          <option value="1000000">$1,000,000</option>
          <option value="2000000">$2,000,000</option>
        </Form.Select>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="glAgg">GL Aggregate</Form.Label>
        <Form.Select id="glAgg" value={glAggregate} onChange={(e) => setGlAggregate(e.target.value)} required {...vp("glAggregate", V.glAggregate)}>
          <option value="1000000">$1,000,000</option>
          <option value="2000000">$2,000,000</option>
          <option value="4000000">$4,000,000</option>
        </Form.Select>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="toolsLimit">Tools & Equipment Limit</Form.Label>
        <InputGroup hasValidation>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control id="toolsLimit" inputMode="numeric" value={toolsLimit} onChange={(e) => setToolsLimit(e.target.value)} placeholder="e.g., 10000" required {...vp("toolsLimit", V.toolsLimit)} />
        </InputGroup>
      </Col>
      <Col md={6}>
        <Form.Label htmlFor="ded">Deductible</Form.Label>
        <Form.Select id="ded" value={deductible} onChange={(e) => setDeductible(e.target.value as Deductible)} required {...vp("deductible", V.deductible)}>
          <option value="">Select</option>
          <option value="0">$0</option>
          <option value="250">$250</option>
          <option value="500">$500</option>
          <option value="1000">$1,000</option>
          <option value="2500">$2,500</option>
        </Form.Select>
      </Col>

      <Col md={12} className="pt-2">
        <div className="d-flex flex-wrap gap-3">
          <Form.Check id="inland" type="checkbox" label="Inland Marine (tools/floaters)" checked={inlandMarine === "yes"} onChange={(e) => setInlandMarine(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="auto" type="checkbox" label="Commercial Auto" checked={commercialAuto === "yes"} onChange={(e) => setCommercialAuto(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="wc" type="checkbox" label="Workers' Comp" checked={workersComp === "yes"} onChange={(e) => setWorkersComp(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="cois" type="checkbox" label="Need COIs / AI endorsements" checked={needCOIs === "yes"} onChange={(e) => setNeedCOIs(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={12}><hr className="my-2" /></Col>

      <Col md={12}>
        <Form.Label>Safety Practices</Form.Label>
        <div className="d-flex flex-wrap gap-3 pt-1">
          <Form.Check id="osha" type="checkbox" label="OSHA 10/30 training" checked={oshaTraining === "yes"} onChange={(e) => setOshaTraining(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="ppe" type="checkbox" label="PPE enforced" checked={ppeEnforced === "yes"} onChange={(e) => setPpeEnforced(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="loto" type="checkbox" label="Lockout/Tagout (LOTO)" checked={lockoutTagout === "yes"} onChange={(e) => setLockoutTagout(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="arc" type="checkbox" label="Arc-flash PPE" checked={arcFlashPPE === "yes"} onChange={(e) => setArcFlashPPE(e.currentTarget.checked ? "yes" : "no")} />
          <Form.Check id="permits" type="checkbox" label="Permits pulled when required" checked={permitPulled === "yes"} onChange={(e) => setPermitPulled(e.currentTarget.checked ? "yes" : "no")} />
        </div>
      </Col>

      <Col md={3}>
        <Form.Label htmlFor="claims">Prior Claims (5y)</Form.Label>
        <Form.Control id="claims" inputMode="numeric" value={priorClaimsCount5y} onChange={(e) => setPriorClaimsCount5y(e.target.value)} placeholder="0" required {...vp("priorClaimsCount5y", V.priorClaimsCount5y)} />
      </Col>

      <Col md={3}>
        <Form.Label htmlFor="lastClaim">Last Claim Date </Form.Label>
        <Form.Control id="lastClaim" type="date" value={lastClaimDate} onChange={(e) => setLastClaimDate(e.target.value)} {...vpOpt("lastClaimDate", !!lastClaimDate, true)} />
        <p>(optional)</p>
      </Col>
    </Row>
  );
};

/* ---------- Notes / Files ---------- */
const SectionNotesFiles: React.FC<{
  values: { notes: string; fileUrls: string };
  setters: { setNotes: (v: string) => void; setFileUrls: (v: string) => void };
  V: any;
  vpOpt: (name: string, valuePresent: boolean, passIfPresent?: boolean) => any;
}> = ({ values, setters, vpOpt }) => {
  const { notes, fileUrls } = values;
  const { setNotes, setFileUrls } = setters;

  return (
    <Row className="g-3">
      <Col md={12}>
        <Form.Label htmlFor="notes">Additional Notes (optional)</Form.Label>
        <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Project types, certifications, special hazards, etc." {...vpOpt("notes", !!notes.trim())} />
      </Col>

      {/* Optional file URLs field (commented to keep form concise)
      <Col md={12}>
        <Form.Label htmlFor="fileUrls">File/Photo URLs (optional)</Form.Label>
        <Form.Control id="fileUrls" as="textarea" rows={2} value={fileUrls} onChange={(e) => setFileUrls(e.target.value)} placeholder="One URL per line (photos, COI requests, schedules, etc.)" {...vpOpt("fileUrls", !!fileUrls.trim())} />
        <div className="form-text">Tip: share public links so advisors can review them.</div>
      </Col>
      */}
    </Row>
  );
};

/* ---------- Simple FAQ (local, zero dependency) ---------- */
type SimpleFaqItem = { id: number; question: string; answer: string; category?: string; type?: string; };
const SimpleFAQ: React.FC<{ faqs: SimpleFaqItem[]; openFirst?: boolean; answerClassName?: string }> = ({ faqs, openFirst, answerClassName }) => {
  const [openIdx, setOpenIdx] = useState(openFirst ? 0 : -1);
  return (
    <div className="accordion" id="faqAccordion">
      {faqs.map((f, i) => (
        <div className="accordion-item" key={f.id}>
          <h2 className="accordion-header">
            <button
              className={"accordion-button " + (openIdx === i ? "" : "collapsed")}
              type="button"
              onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
            >
              {f.question}
            </button>
          </h2>
          <div className={"accordion-collapse collapse " + (openIdx === i ? "show" : "")}>
            <div className={"accordion-body " + (answerClassName || "")}>
              {f.answer}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
/* ==== END PART B ==== */


/* ==== PART C: Confirmation + FAQ content + tiny helpers + default export ==== */

const ConfirmationBlock: React.FC<{
  status: { ok: boolean; status?: number; id?: string; message?: string } | null;
  app: ILeadPayload | null;
}> = ({ status, app }) => {
  const ok = !!status?.ok;

  if (!app) {
    return (
      <Card className="border-0">
        <Card.Body className="p-4">
          <Alert variant="warning" className="mb-0">We don’t have your details to display yet.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const listify = (arr?: string[]) => (arr && arr.length ? arr.map((x) => String(x).replace(/_/g, " ")).join(", ") : "—");

  return (
    <Card className="border-0">
      <Card.Body className="p-4">
        {ok ? (
          <Alert variant="success" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">Thank you! A licensed advisor will contact you shortly.</div>
              <div className="small">
                Submission status: <strong>{status?.status ?? 200}</strong>
                {status?.id ? <> • Reference ID: <strong>{status.id}</strong></> : null}
              </div>
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="d-flex align-items-start">
            <div>
              <div className="fw-bold mb-1">We couldn’t confirm your submission.</div>
              <div className="small">{status?.message || "Please try again or give us a call."}</div>
            </div>
          </Alert>
        )}

        <div className="mt-3">
          <Section title="Contact & Business">
            <KV label="Name" value={dash(app.fullName)} />
            <KV label="Email" value={dash(app.email)} />
            <KV label="Phone" value={dash(app.phone)} />
            <KV label="Business" value={dash(app.businessName)} />
            <KV label="Years in Business" value={dash(app.yearsInBusiness)} />
            <KV label="Employees" value={dash(app.employeesCount)} />
            <KV label="Website" value={dash(app.website)} />
          </Section>

          <Section title="Address">
            <KV label="Street" value={dash(app.address1)} />
            <KV label="Unit / Suite" value={dash(app.address2)} />
            <KV label="City / State / Zip" value={`${dash(app.city)}, ${dash(app.state)} ${dash(app.zipCode)}`} />
          </Section>

          <Section title="Operations & Exposures">
            <KV label="Environment" value={dash(app.workEnv)} />
            <KV label="Services" value={listify(app.services as unknown as string[])} />
            <KV label="License" value={`${dash(app.licenseState)} ${dash(app.licenseNumber)}`} />
            <KV label="Height Work" value={dash(app.workAtHeights)} />
            <KV label="New Construction" value={dash(app.newConstruction)} />
            <KV label="Service Calls" value={dash(app.serviceCalls)} />
            <KV label="Knob-and-Tube" value={dash(app.knobAndTube)} />
            <KV label="Aluminum Wiring" value={dash(app.aluminumWiring)} />
            <KV label="Mobile Radius" value={app.mobileRadiusMiles ? `${app.mobileRadiusMiles} miles` : "—"} />
            <KV label="Annual Revenue" value={money(app.annualRevenue)} />
            <KV label="Employee Payroll" value={money(app.employeePayroll)} />
            <KV label="Subcontractor Annual Cost" value={money(app.subcontractorAnnualCost)} />
          </Section>

          <Section title="Coverage & Safety">
            <KV label="GL Per-Occurrence" value={money(app.glPerOccurrence)} />
            <KV label="GL Aggregate" value={money(app.glAggregate)} />
            <KV label="Tools & Equipment" value={money(app.toolsLimit)} />
            <KV label="Inland Marine" value={dash(app.inlandMarine)} />
            <KV label="Commercial Auto" value={dash(app.commercialAuto)} />
            <KV label="Workers' Comp" value={dash(app.workersComp)} />
            <KV label="Deductible" value={app.deductible ? money(app.deductible) : "—"} />
            <KV label="Additional Insureds" value={dash(app.additionalInsureds)} />
            <KV label="COIs Needed" value={dash(app.needCOIs)} />
            <KV label="OSHA Training" value={dash(app.oshaTraining)} />
            <KV label="PPE Enforced" value={dash(app.ppeEnforced)} />
            <KV label="Lockout/Tagout" value={dash(app.lockoutTagout)} />
            <KV label="Arc-flash PPE" value={dash(app.arcFlashPPE)} />
            <KV label="Permits Pulled" value={dash(app.permitPulled)} />
            <KV label="Prior Claims (5y)" value={dash(app.priorClaimsCount5y)} />
            <KV label="Last Claim Date" value={dash(app.lastClaimDate)} />
          </Section>

          <Section title="Notes / Files">
            <div className="p-3 bg-body-tertiary border rounded mb-2">{dash(app.notes)}</div>
            <KV label="File/Photo URLs" value={dash(app.fileUrls)} />
          </Section>
        </div>

        <div className="mt-4 d-flex flex-wrap gap-2">
          <Button href="tel:3033375434" variant="primary">Call {AGENT_PHONE_NUMBER}</Button>
          <Button variant="outline-secondary" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>Back to top</Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ---------- Tiny helpers for confirmation layout ---------- */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-4">
    <h6 className="text-uppercase text-muted mb-2">{title}</h6>
    <Row as="dl" className="row-cols-1 row-cols-md-2 g-3 mb-0">{children}</Row>
    <hr className="mt-3 mb-0" />
  </div>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Col className="mb-0">
    <dt className="small text-muted mb-1">{label}</dt>
    <dd className="mb-0">{value}</dd>
  </Col>
);

/* ---------- FAQ Content ---------- */
export const ELECTRICIAN_INSURANCE_FAQ_CONTENT_v01: SimpleFaqItem[] = [
  { id: 1, question: "What insurance do electricians typically need?", answer: "Most electricians start with General Liability (GL) for third‑party injury/property damage. Depending on your operation, you might add Inland Marine (tools & equipment), Commercial Auto, Workers’ Comp, and Additional Insured/COI endorsements required by GC contracts." },
  { id: 2, question: "How much GL limit should I choose?", answer: "Common limits are $1M per occurrence / $2M aggregate. Larger commercial or public jobs sometimes require $2M/$4M. We’ll help match requirements to your contracts." },
  { id: 3, question: "Does tools & equipment insurance cover theft from my truck?", answer: "Inland Marine (a.k.a. tool floaters) can cover tools on the jobsite, in transit, and stored in vehicles—subject to policy terms, limits, and any theft safeguards (e.g., locked vehicle, alarm)." },
  { id: 4, question: "Can I get a certificate of insurance (COI) with Additional Insured language?", answer: "Yes. We can add blanket Additional Insured and Waiver of Subrogation endorsements when available, and issue COIs quickly once coverage is bound." },
  { id: 5, question: "Will my rate be higher if I work on aluminum wiring or knob‑and‑tube?", answer: "Higher‑risk tasks can affect pricing and eligibility. Disclosing these helps us match carriers appropriately so there are no surprises at binding or claim time." },
  { id: 6, question: "Do I need Workers’ Comp if I use only subcontractors?", answer: "Rules vary by state. If you have no W‑2 employees, you may not need WC, but some GCs require it anyway. We’ll help you navigate state rules and contract requirements." },
  { id: 7, question: "Are EV charger installs or solar tie‑ins covered?", answer: "They can be, but they must be disclosed and included in your operations description. Some carriers require specific endorsements for EVSE or PV interconnect work." },
  { id: 8, question: "How fast can I get coverage?", answer: "Many small policies can be bound same‑day if we have complete information. Complex risks or higher limits may take longer due to underwriting review." },
  { id: 9, question: "What information do you need for a quote?", answer: "Basic contact info, business details, services performed, revenue/payroll, prior claims, and the coverage/limits you want. COI/endorsement requirements from your GC are helpful too." },
  { id: 10, question: "Can I save money by bundling?", answer: "Often yes. Placing GL + Tools/Inland Marine + Commercial Auto and/or WC with compatible carriers can unlock credits and streamline COI issuance." },
];

/* ---------- Default export ---------- */
export default DISCOUNT_ELECTRICIAN_LandingPage_v020c_WIZARD;
/* ==== END PART C ==== */

